import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Bookmark, Share2, MoreHorizontal, EyeOff, Flag, X, Trophy, Lock, Sparkles, Pin, Pencil, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";
import { MediaView } from "@/components/MediaView";
import { CommentsSheet, ReportDialog } from "@/components/Sheets";
import { BussaButton, FollowButton } from "@/components/Bussa";
import { Avatar, DemoChip } from "@/components/Common";

function ActionBtn({ icon: Icon, label, count, active, onClick, testId }: any) {
  return (
    <button onClick={onClick} data-testid={testId} aria-label={label} className="flex flex-col items-center gap-1 group">
      <span className={`w-12 h-12 rounded-full flex items-center justify-center bg-[hsl(var(--background)/.45)] backdrop-blur-md border ${active ? "border-[hsl(var(--magenta)/.6)] text-[hsl(var(--magenta))] bg-[hsl(var(--magenta)/.12)]" : "border-white/12 group-hover:bg-[hsl(var(--background)/.7)]"}`}>
        <Icon size={22} fill={active ? "currentColor" : "none"} />
      </span>
      {count !== undefined && <span className="text-xs font-semibold">{count}</span>}
    </button>
  );
}

function CreatorPanel({ contentId, onClose }: any) {
  const [c, setC] = useState<any>(null);
  useEffect(() => { api.get(`/contents/${contentId}/creator`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))); }, [contentId]);
  return (
    <div
      className="absolute inset-x-2 bottom-2 md:bottom-4 md:left-auto md:right-4 md:w-96 z-20 rounded-3xl bg-[hsl(var(--surface)/.95)] backdrop-blur-2xl border border-[hsl(var(--border))] shadow-[0_24px_60px_-20px_hsl(230_50%_3%/.9)] p-5" data-testid="creator-panel" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} aria-label="Chiudi" className="absolute top-4 right-4 text-white/60 hover:text-white" data-testid="creator-panel-close"><X size={20} /></button>
      <div className="label text-primary mb-3">Chi c'è dietro</div>
      {!c ? <div className="h-20" /> : (
        <div className="space-y-4">
          <Link to={`/u/${c.username}`} className="flex items-center gap-4 group" data-testid="creator-profile-link">
            <Avatar user={c} size={56} ring />
            <div className="min-w-0">
              <div className="font-display text-lg font-semibold truncate group-hover:text-primary transition-colors">{c.display_name}</div>
              <div className="text-white/60 text-sm">@{c.username}{c.wins > 0 && <span className="ml-2 text-primary inline-flex items-center gap-1"><Trophy size={12} />{c.wins}</span>}</div>
            </div>
          </Link>
          {c.bio && <p className="text-sm text-white/70 line-clamp-2">{c.bio}</p>}
          {c.is_demo && <DemoChip />}
          <div className="flex gap-2 flex-wrap items-start">
            <BussaButton userId={c.id} relation={c.relation} onChange={(r: any) => setC({ ...c, relation: r })} compact />
            <FollowButton userId={c.id} relation={c.relation} onChange={(r: any) => setC({ ...c, relation: r })} compact />
            <Link to={`/u/${c.username}`} className="btn btn-ghost px-4 py-2 text-sm" data-testid="creator-view-profile-btn">Profilo</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export function FeedCard({ item, muted, onMute, onHide }: any) {
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);
  const [c, setC] = useState(item);
  const [reveal, setReveal] = useState(false);
  const [comments, setComments] = useState(false);
  const [report, setReport] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting && e.intersectionRatio >= 0.6), { threshold: [0, 0.6, 1] });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const like = async () => {
    const was = c.liked;
    setC({ ...c, liked: !was, like_count: c.like_count + (was ? -1 : 1) });
    try {
      const { data } = was ? await api.delete(`/contents/${c.id}/like`) : await api.post(`/contents/${c.id}/like`);
      setC((x: any) => ({ ...x, ...data }));
    } catch (e) {
      setC((x: any) => ({ ...x, liked: was, like_count: x.like_count + (was ? 1 : -1) }));
      toast.error(errMsg(e));
    }
  };
  const save = async () => {
    const was = c.saved;
    setC({ ...c, saved: !was });
    try { was ? await api.delete(`/contents/${c.id}/save`) : await api.post(`/contents/${c.id}/save`); if (!was) toast.success("Salvato"); }
    catch (e) { setC((x: any) => ({ ...x, saved: was })); toast.error(errMsg(e)); }
  };
  const share = async () => {
    const url = `${window.location.origin}/c/${c.id}`;
    try {
      if (navigator.share) await navigator.share({ title: "UNSEEN", url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copiato"); }
    } catch { /* annullato */ }
  };
  const ownAction = async (a: string) => {
    try {
      if (a === "feature") { const { data } = await api.post(`/contents/${c.id}/feature`); setC((x: any) => ({ ...x, featured: data.featured })); toast.success(data.featured ? "In evidenza sul profilo" : "Tolto da In evidenza"); }
      if (a === "pin") { const { data } = await api.post(`/contents/${c.id}/pin`); setC((x: any) => ({ ...x, pinned: data.pinned })); toast.success(data.pinned ? "Fissato in alto" : "Non più fissato"); }
      if (a === "edit") {
        const caption = window.prompt("Nuova descrizione", c.caption);
        if (caption === null) return;
        const { data } = await api.patch(`/contents/${c.id}`, { caption, category: c.category, hashtags: c.hashtags || [], media_ids: c.media.map((m: any) => m.id) });
        setC((x: any) => ({ ...x, caption: data.caption, hashtags: data.hashtags }));
        toast.success("Descrizione aggiornata");
      }
      if (a === "delete") {
        if (!window.confirm("Eliminare definitivamente questo contenuto?")) return;
        await api.delete(`/contents/${c.id}`);
        toast.success("Contenuto eliminato");
        if (onHide) onHide(c.id); else if ((window.history.state?.idx ?? 0) > 0) window.history.back(); else nav("/profilo", { replace: true });
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const less = async () => {
    await api.post(`/contents/${c.id}/less`).catch(() => {});
    toast("Ne vedrai meno così.");
    onHide?.(c.id);
  };
  return (
    <section ref={ref} className="relative h-full w-full snap-start snap-always overflow-hidden bg-black" data-testid="feed-item" data-content-id={c.id}>
      <MediaView content={c} active={active} muted={muted} onMute={onMute} />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
      <div className="absolute right-3 bottom-28 md:bottom-10 flex flex-col gap-4 z-10">
        <ActionBtn icon={Heart} label="Mi piace" count={c.like_count} active={c.liked} onClick={like} testId="like-btn" />
        <ActionBtn icon={MessageCircle} label="Commenti" count={c.comment_count} onClick={() => setComments(true)} testId="comments-btn" />
        <ActionBtn icon={Bookmark} label="Salva" active={c.saved} onClick={save} testId="save-btn" />
        <ActionBtn icon={Share2} label="Condividi" onClick={share} testId="share-btn" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button aria-label="Altro" data-testid="more-btn" className="w-12 h-12 rounded-full flex items-center justify-center bg-black/35 backdrop-blur-md border border-white/10 hover:bg-black/55"><MoreHorizontal size={22} /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-white/10 rounded-2xl">
            {c.is_mine ? (<>
              <DropdownMenuItem onClick={() => ownAction("feature")} data-testid="own-feature-btn"><Sparkles size={14} className="mr-2" />{c.featured ? "Togli da In evidenza" : "Metti in evidenza"}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => ownAction("pin")} data-testid="own-pin-btn"><Pin size={14} className="mr-2" />{c.pinned ? "Non fissare più" : "Fissa in alto"}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => ownAction("edit")} data-testid="own-edit-btn"><Pencil size={14} className="mr-2" />Modifica descrizione</DropdownMenuItem>
              <DropdownMenuItem onClick={() => ownAction("delete")} data-testid="own-delete-btn"><Trash2 size={14} className="mr-2" />Elimina</DropdownMenuItem>
            </>) : (<>
              <DropdownMenuItem onClick={less} data-testid="show-less-btn"><EyeOff size={14} className="mr-2" />Mostramene meno</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setReport(true)} data-testid="report-btn"><Flag size={14} className="mr-2" />Segnala</DropdownMenuItem>
            </>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="absolute left-0 right-20 bottom-20 md:bottom-6 px-5 z-10 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <Link to={`/categoria/${c.category}`} className="chip chip-accent backdrop-blur" data-testid="feed-category-chip">{c.category}</Link>
          {c.contest_id && <Link to={`/daily/${c.contest_id}`} className="chip backdrop-blur"><Trophy size={11} />In gara</Link>}
          {c.visibility === "connections" && <span className="chip backdrop-blur"><Lock size={11} />Connessioni</span>}
        </div>
        {c.caption && <p className="text-sm md:text-base line-clamp-3 max-w-xl" data-testid="feed-caption">{c.caption}</p>}
        {c.hashtags?.length > 0 && <div className="flex gap-2 flex-wrap text-sm text-white/70">{c.hashtags.slice(0, 5).map((h: string) => <Link key={h} to={`/tag/${h}`} className="hover:text-primary">#{h}</Link>)}</div>}
        {!c.is_mine && (
          <button onClick={() => setReveal(true)} data-testid="reveal-creator-btn" className="btn bg-white/10 border-white/15 backdrop-blur-md px-4 py-2 text-sm hover:bg-white/20"><span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary to-[hsl(var(--violet))]" />{t("feed.reveal")}</button>
        )}
      </div>
      {reveal && <CreatorPanel contentId={c.id} onClose={() => setReveal(false)} />}
      <CommentsSheet contentId={c.id} open={comments} onOpenChange={setComments} onCount={(d: number) => setC((x: any) => ({ ...x, comment_count: x.comment_count + d }))} />
      <ReportDialog contentId={c.id} open={report} onOpenChange={setReport} />
    </section>
  );
}
